import type { URLParams } from '@archipro-website/top-navigation';
import {
    LEGACY_PATH_PARAM_KEYS,
    generateURL,
    getBoolean,
    getNumber,
    getNumberRange,
    getString,
    getStringArray,
    parseURL,
} from '@archipro-website/top-navigation';
import type { Category } from '@archipro-design/aria';
import {
    convertToProductSuppliersUrl,
    isProductSuppliersPage,
} from '~/modules/url';
import type { CategoryFilterState } from '@modules/search/components/filter-drawer/FilterCategorySelector';
import type { CheckboxFilterState } from '@modules/search/components/filter-drawer/FilterCheckbox';
import type { CheckboxGroupFilterState } from '@modules/search/components/filter-drawer/FilterCheckboxGroup';
import type { RangeSelectorFilterState } from '@modules/search/components/filter-drawer/FilterRangeSelector';
import type { SearchListFilterState } from '@modules/search/components/filter-drawer/FilterSearchList';
import type { LocationFilterState } from '@modules/search/components/filter-drawer/FilterLocation';
import type { RadioGroupFilterState } from '@modules/search/components/filter-drawer/FilterRadioGroup';
import type { AwardFilterState } from '@modules/search/components/awards-filter-tree/AwardsFilterTree';
import {
    LOCATION_SEARCH_PARAM_KEYS,
    SEARCH_CATEGORIES,
    SEARCH_QUERY_KEY,
} from '@modules/search/config';
import type {
    BusinessFocusType,
    CheckboxFilterKeys,
    FacetOptionsFilterParams,
    FilterSortOption,
    RangeFilterKeys,
    SearchIndex,
    SearchListFilterKeys,
    SearchLocation,
    SearchServiceFilters,
    SearchServiceRequest,
    VisibilityType,
} from '../type/api';
import type {
    FacetOptionFilterState,
    FilterStates,
    FilterStatesKey,
} from '../type/filter-state';
import { DEFAULT_SEARCH_LIST_SIZE } from './create-es-aggregation-params';
import { USER_PREFERENCE_KEY } from '../hook/use-search-filters';
import { DEFAULT_PAGE_SIZE } from '../components/search-results-grid/SearchResultsGrid';
import { applyESSearchSort } from './apply-es-search-sort';
import { getSearchIndexFromCategory } from './search-categories';
import { toSearchCoordinate } from './search-coordinate';
import { hasSortParam } from '~/modules/seo/util/has-sort-param';
import {
    getProfessionalsSortDefaults,
    shouldWriteSortParam,
} from '@modules/category/util/directory-default-sort';

const URL_PARAM_MAP: Record<string, string> = {
    professions: 'profession',
    professionals: 'company',
    libraryRanges: 'productRanges',
    sortBy: 'sort',
    isPurchasable: 'forSale',
    category: 'categoryLink',
    state: LOCATION_SEARCH_PARAM_KEYS.state,
};

const LEGACY_PATH_PARAM_KEYS_EXCLUDING_COMPANY = LEGACY_PATH_PARAM_KEYS.filter(
    (key) => key !== 'company',
);

const RANGE_DIVISORS: Record<RangeFilterKeys, number> = {
    priceRange: 100,
    lengthRange: 1000,
    widthRange: 1000,
    heightRange: 1000,
    diameterRange: 1000,
    weightRange: 1000,
};

export const DEFAULT_SOURCE_FILTER = [
    'id',
    'PType',
    'ArticleWordCount',
    'ArticleAuthor',
    'ArticleByArchiPro',
    'Categories',
    'CompanyLogos',
    'Created',
    'Country',
    'Link',
    'LiveDate',
    'CanPurchase',
    'LibraryThumbs',
    'LogoBackgroundColor',
    'ProductColours',
    'ProfessionalID',
    'Professional',
    'ProfessionalLink',
    'Thumb_lg',
    'Title',
    'DirectoryTile',
    'Services',
    'SearchLocations',
    'PricingDisplay',
    'MemberPricing',
    'TradePricing',
    'Thumbnail',
    'Email',
    'Phone',
    'Phone2',
    'HasAwards',
    'Visibilities',
    'DirectoryTiles',
    'TopBanner',
    'DownloadFileCategories',
    'SEODescription',
    'TotalViewedCount',
    'TotalSavedCount',
];

// The six name levels only: neither the slug nor the coordinate belongs in a
// URL level round-trip.
const ORDERED_LOCATION_KEY: (
    | 'suburb'
    | 'district'
    | 'city'
    | 'region'
    | 'state'
    | 'country'
)[] = ['suburb', 'district', 'city', 'region', 'state', 'country'];

/**
 * Create a new URL based on the filters' state and original URL
 * @param filterStates      the filter states
 * @param originalURL
 * @param featureSet
 * @param ignoreFilterKeys  we will ignore these keys when parsing the filter states
 * @param updateUserPrefs   add additional updateUserPrefs parameter if it's true
 * @returns
 */
export function generateURLByFilterState(
    filterStates: FilterStates,
    originalURL: string,
    ignoreFilterKeys: FilterStatesKey[] = [],
    updateUserPrefs = false,
): string {
    const isCategoryPage = !!findSearchCategoryByLinkPrefix(originalURL);

    const {
        basePath,
        params: originalParams,
        isSuppliersEndpoint,
    } = parseURL(originalURL);

    // Check if we're on the suppliers page
    const isSuppliersPage = isProductSuppliersPage(originalURL);

    const professionalsSortDefaults = getProfessionalsSortDefaults(
        originalURL,
        filterStates.searchLocation?.selectedLocation,
    );

    // we will not add the category param if it's category page
    // instead, we will create new path based on the category state
    const params = convertFilterStateToURLParams(
        filterStates,
        isCategoryPage ? [...ignoreFilterKeys, 'category'] : ignoreFilterKeys,
        isSuppliersPage,
        {
            destinationDefault:
                professionalsSortDefaults?.destinationDefault ??
                filterStates.defaultSortBy,
            currentDefault:
                professionalsSortDefaults?.currentDefault ??
                filterStates.defaultSortBy,
            originalHadExplicitSort: hasSortParam(originalParams),
        },
    );

    if (updateUserPrefs) {
        params[USER_PREFERENCE_KEY] = 1;
    }

    // Preserve browse parameter from original URL
    try {
        const urlObj = new URL(originalURL, 'http://dummy');
        const browseParam = urlObj.searchParams.get('browse');
        if (browseParam) {
            params.browse = browseParam;
        }
    } catch {
        // If URL parsing fails, continue without browse param
    }

    // keep original info for the non category page
    if (!isCategoryPage) {
        if (originalParams?.category) {
            params.category = originalParams.category;
        } else {
            if (params.forSale || params.downloads) {
                params.category = 'products';
            }
        }
        if (originalParams?.search) {
            params.search = originalParams.search;
        }
        // Professional detail tabs keep their in-page search in the `q` param
        // (not `search`); preserve it so applying a filter doesn't silently
        // drop an active search.
        if (originalParams?.q) {
            params.q = originalParams.q;
        }
    }

    const categoryState = filterStates.category;

    // create new path based on the category state for the category page
    const newPath = isCategoryPage
        ? (categoryState?.selectedCategory?.id ?? basePath)
        : basePath;

    return generateURL(
        isSuppliersEndpoint ? convertToProductSuppliersUrl(newPath) : newPath,
        params,
        true,
        LEGACY_PATH_PARAM_KEYS_EXCLUDING_COMPANY,
        getPathFilterKeysByCategory(
            categoryState?.selectedCategory,
            isCategoryPage,
        ),
    );
}

/**
 * Find a search category whose categoryLink is a prefix of the given path.
 */
function findSearchCategoryByLinkPrefix(path: string) {
    return SEARCH_CATEGORIES.find(
        (cfg) => cfg.categoryLink && path.startsWith(cfg.categoryLink),
    );
}

export function getPathFilterKeysByCategory(
    category?: Category | null,
    isCategoryPage = false,
): Readonly<Record<string, string>> {
    if (!isCategoryPage || !category) return {};
    const keys: Record<string, string> = {
        materials: 'material',
        colors: 'color',
    };
    if (category.id.startsWith('/professionals/')) {
        keys['location'] = 'location';
    }

    return keys;
}

function convertCategoryFilter(state: CategoryFilterState): URLParams {
    const categoryLink = state.selectedCategory?.id ?? state.category.id;
    if (!categoryLink || categoryLink === state.category.id) {
        return {};
    }
    return { categoryLink };
}

function convertCheckboxFilter(
    filterKey: string,
    state: CheckboxFilterState,
): URLParams {
    if (!state.checked) {
        return {};
    }
    return { [filterKey]: 1 };
}

function convertCheckboxGroupFilter(
    filterKey: string,
    state: CheckboxGroupFilterState,
): URLParams {
    if (!state.checkedItems?.length) {
        return {};
    }
    const values = state.checkedItems
        .filter((item) => !!item.value)
        .map((item) => item.value!);
    return { [filterKey]: values };
}

function convertAwardsFilter(
    filterKey: string,
    state: AwardFilterState,
): URLParams {
    if (!state.checkedItems?.length) {
        return {};
    }
    if (state.checkedItems.some((item) => item.value === 'all')) {
        return { [filterKey]: ['all'] };
    }
    const values = state.checkedItems
        .filter((item) => !!item.value)
        .map((item) => item.value!);
    return { [filterKey]: values };
}

function convertRangeSelectorFilter(
    filterKey: string,
    state: RangeSelectorFilterState,
): URLParams {
    if (!state.value) {
        return {};
    }

    const { min, max } = state.value;
    const { min: availableMin, max: availableMax } = state.range;
    if (min === availableMin && max === availableMax) {
        return {};
    }
    return { [filterKey]: [min, max] };
}

function convertSearchListFilter(
    filterKey: string,
    state: SearchListFilterState,
    subFilterKey?: string,
    encode?: boolean,
): URLParams {
    const params: URLParams = {};

    if (state.search) {
        // TODO: Fix this when we allow search for facet filters.
        params[`${filterKey}_search`] = `${
            subFilterKey ? `${subFilterKey}-` : ''
        }${state.search}`;
    }
    if (state.items.length && state.items.length > DEFAULT_SEARCH_LIST_SIZE) {
        params[`${filterKey}_size`] = state.items.length;
    }
    const checkedItems = state?.selectedItems;

    if (!checkedItems?.length) {
        return params;
    }

    params[filterKey] = checkedItems.map(
        (item) =>
            `${subFilterKey ? `${subFilterKey}-` : ''}${
                encode ? encodePathValue(item.value) : item.value
            }`,
    );
    return params;
}

function convertRadioGroupFilter(
    filterKey: string,
    state: RadioGroupFilterState,
): URLParams {
    if (!state.selectedItem?.value || state.selectedItem.isDefault) {
        return {};
    }
    return { [filterKey]: state.selectedItem.value };
}

function convertLocationFilter(state: LocationFilterState): URLParams {
    const params: URLParams = {};

    if (!state.selectedLocation) {
        return params;
    }

    const location = state.selectedLocation;

    if (location.slug) {
        params['location'] = location.slug;
        return params;
    }

    const values: string[] = [];
    ORDERED_LOCATION_KEY.forEach((key) => {
        const value = (location[key] ?? '').toLocaleLowerCase();
        // ignore lower level values if they are empty
        if (!value && !values.length) {
            return;
        }
        const paramKey = LOCATION_SEARCH_PARAM_KEYS[key];
        params[paramKey] = value ?? '';
        values.push(value);
    });

    // convert to search params
    return params;
}

function convertFilterStateToURLParams(
    filterStates: FilterStates,
    ignoreFilterKeys: (keyof FilterStates)[] = [],
    isSuppliersPage = false,
    sortWrite?: {
        destinationDefault?: string | null;
        currentDefault?: string | null;
        originalHadExplicitSort?: boolean;
    },
): URLParams {
    let params: URLParams = {};
    const { sortBy, defaultSortBy } = filterStates;

    if (
        sortBy &&
        shouldWriteSortParam({
            sortBy,
            currentDefault: sortWrite?.currentDefault ?? defaultSortBy,
            destinationDefault: sortWrite?.destinationDefault ?? defaultSortBy,
            originalHadExplicitSort: sortWrite?.originalHadExplicitSort ?? true,
        })
    ) {
        params[getMappingKeyName('sortBy')] = sortBy;
    }

    Object.keys(filterStates).forEach((filterKey) => {
        if (
            [
                ...ignoreFilterKeys,
                'queryFilters',
                'visibilityType',
                'totalCount',
                'sortBy',
                'defaultSortBy',
            ].find((ik) => ik === filterKey)
        ) {
            return;
        }

        const paramKey = filterKey as Exclude<
            keyof FilterStates,
            'queryFilters' | 'visibilityType' | 'totalCount'
        >;
        const urlParamKey = getMappingKeyName(paramKey, isSuppliersPage);

        if (paramKey === 'facetOptions') {
            params = {
                ...params,
                ...convertFacetFilterStates(
                    urlParamKey,
                    filterStates.facetOptions,
                ),
            };
            return;
        }

        const state = filterStates[paramKey];
        if (!state) {
            return;
        }

        if (typeof state === 'boolean') {
            return;
        }

        if (typeof state === 'string' || Array.isArray(state)) {
            params[urlParamKey] = state;
            return;
        }

        params = {
            ...params,
            ...convertFilterByState(urlParamKey, state),
        };
    });

    return params;
}

function convertFilterByState(
    filterKey: string,
    state:
        | CheckboxFilterState
        | CheckboxGroupFilterState
        | SearchListFilterState
        | RadioGroupFilterState
        | RangeSelectorFilterState
        | CategoryFilterState
        | LocationFilterState
        | AwardFilterState,
    subFilterKey?: string,
): URLParams {
    switch (state.kind) {
        case 'CategorySelector':
            return convertCategoryFilter(state);
        case 'Checkbox':
            return convertCheckboxFilter(filterKey, state);
        case 'CheckboxGroup':
            return convertCheckboxGroupFilter(filterKey, state);
        case 'RangeSelector':
            return convertRangeSelectorFilter(filterKey, state);
        case 'SearchList':
            return convertSearchListFilter(
                filterKey,
                state,
                subFilterKey,
                filterKey === 'materials',
            );
        case 'RadioGroup':
            return convertRadioGroupFilter(filterKey, state);
        case 'Location':
            return convertLocationFilter(state);
        case 'AwardsFilter':
            return convertAwardsFilter(filterKey, state);
        default:
            throw new Error(
                '[filter-url-helpers] convertFilterByState: unsupported filter state:' +
                    JSON.stringify(state),
            );
    }
}

/**
 * Convert facet filter states to URL params.
 */
function convertFacetFilterStates(
    filterKey: string,
    states: FacetOptionFilterState[] | undefined,
): URLParams {
    if (!states?.length) {
        return {};
    }
    return states.reduce<URLParams>((params, facetState) => {
        const facetParams = convertFilterByState(
            filterKey,
            facetState.state,
            facetState.id,
        );
        const existing = params[filterKey];
        const next = facetParams[filterKey];
        if (Array.isArray(existing) && Array.isArray(next)) {
            // Merge filters from multiple facets into one array.
            return {
                ...params,
                ...facetParams,
                [filterKey]: [...(existing as string[]), ...(next as string[])],
            };
        }
        return { ...params, ...facetParams };
    }, {});
}

/**
 * Generate the query params for querying the Search Service from the URL and arguments.
 * @param url The URL to parse.
 * @param searchType The search type to override. If not provided, tries to get it from the URL.
 * @param visibilityType The page to search for.
 * @param overrideParams The URL search params to override.
 * @param defaultSort The default sort to apply.
 * @param overrideFilters The Search Service query filters to override.
 * @returns The query params for the Search Service.
 */
export function parseURLToESQueryParams(
    url: string,
    searchType?: SearchIndex,
    visibilityType?: VisibilityType,
    overrideParams?: URLParams,
    defaultSort?: FilterSortOption,
    overrideFilters?: SearchServiceFilters,
) {
    const { basePath, params: originalParams } = parseURL(url);
    const params = { ...originalParams, ...overrideParams };

    const queryParams: SearchServiceRequest = {
        search: getString(params, SEARCH_QUERY_KEY) ?? '',
        visibilityType,
        sourceFilter: DEFAULT_SOURCE_FILTER,
        size: getNumber(params, 'pageSize') || DEFAULT_PAGE_SIZE,
    };

    const page = Math.max((getNumber(params, 'page') ?? 0) - 1, 0);
    if (page) queryParams.page = page;

    const urlFilters = createESFiltersFromURLParams(basePath, params);
    const merged = { ...urlFilters, ...overrideFilters };
    // Applied after the override merge so no caller can reintroduce an
    // unscoped-directory ordering alongside a location filter.
    const filters = merged.searchLocation
        ? withoutUnscopedDirectoryOrdering(merged)
        : merged;
    const sortBy = applyESSearchSort(url, filters, defaultSort);

    if (Object.keys(filters).length) queryParams.filters = filters;

    const searchIndex = searchType ?? getSearchIndex(basePath, params);
    if (searchIndex) queryParams.searchType = searchIndex;

    if (sortBy) queryParams.sortBy = sortBy;

    return { queryParams };
}

function getSearchIndex(
    basePath: string,
    params: URLParams,
): SearchIndex | undefined {
    const category = getString(params, 'category');
    const searchIndex = getSearchIndexFromCategory(category);
    if (searchIndex) {
        return searchIndex;
    }
    return findSearchCategoryByLinkPrefix(basePath)?.index;
}

function createESFiltersFromURLParams(
    basePath: string,
    params: URLParams,
): SearchServiceFilters {
    const filters: SearchServiceFilters = {};

    // get from base path
    const categoryLink = getCategoryLink(basePath, params);

    if (categoryLink) {
        filters.categoryLink = categoryLink;
    } else if (
        // On the root professionals directory, sort by architects first.
        basePath.startsWith('/professionals')
    ) {
        filters.architectsFirst = true;
    }

    addBooleanFilters(
        params,
        [
            'isPurchasable',
            'isOnSale',
            'isInStock',
            'isDeliveryAvailable',
            'isSampleAvailable',
            'hasProducts',
            'hasVideos',
            'hasAwards',
            'projectOfTheMonth',
            'invalidLink',
            'disabled',
            'profileOnly',
        ],
        filters,
    );

    addRangeFilters(
        params,
        [
            'priceRange',
            'lengthRange',
            'widthRange',
            'heightRange',
            'diameterRange',
            'weightRange',
        ],
        filters,
    );

    // Check if we're on suppliers page to determine which param name to use
    const isSuppliersPage = isProductSuppliersPage(basePath);

    // On suppliers page, 'brands' param maps to 'professionals' filter
    // On products page, 'company' param maps to 'professionals' filter
    const professionalsParamKey = isSuppliersPage
        ? 'brands'
        : getMappingKeyName('professionals');

    // Get professionals/brands from the appropriate param
    const professionalsValue = getStringArray(params, professionalsParamKey);
    if (professionalsValue) {
        filters.professionals = professionalsValue;
    }

    // Build the list of filters to process
    // Exclude 'brands' on suppliers page since we've already handled it as 'professionals'
    const stringArrayFilterKeys: SearchListFilterKeys[] = [
        'awards',
        'materials',
        'libraryRanges',
        'associations',
        'professions',
        'designers',
        'professionalCategories',
        'downloads',
    ];

    // Only include 'brands' on products page (not suppliers page)
    if (!isSuppliersPage) {
        stringArrayFilterKeys.push('brands');
    }

    addStringArrayFilters(params, stringArrayFilterKeys, filters, [
        'materials',
    ]);

    const colors = getStringArray(params, getMappingKeyName('colors'));
    if (colors) filters.colors = colors;

    const businessFocuses = getStringArray(params, 'businessFocuses');
    if (businessFocuses)
        filters.businessFocuses = businessFocuses as BusinessFocusType[];

    const facetOptions: FacetOptionsFilterParams[] = [];
    getStringArray(params, 'facetOptions')?.forEach((value) => {
        const [facet, option] = value.split('-');
        if (!facet || !option) return;
        facetOptions.push({
            facet,
            option,
        });
    });
    if (facetOptions.length) filters.facetOptions = facetOptions;

    const projectCount = getNumber(params, 'projectCount');
    if (projectCount) filters.projectCount = projectCount;

    const searchLocation = getLocation(params);
    if (searchLocation) filters.searchLocation = searchLocation;

    return filters;
}

/**
 * Strip orderings that only apply to the unscoped professionals directory.
 *
 * A location-scoped search ranks locally based professionals above those that
 * merely service the area. The architects-first ordering sits above that
 * ranking on the search service side, so sending it alongside a location
 * filter buries every locally based non-architect below all architects. Call
 * this on any filter set that accompanies an applied location.
 */
export function withoutUnscopedDirectoryOrdering<
    T extends { architectsFirst?: unknown },
>(filters: T): Omit<T, 'architectsFirst'> {
    const { architectsFirst: _architectsFirst, ...rest } = filters;
    return rest;
}

function getCategoryLink(
    basePath: string,
    params: URLParams,
): string | undefined {
    const categoryLink = getString(params, 'categoryLink');
    if (categoryLink) {
        return categoryLink;
    }
    const categoryByPath = findSearchCategoryByLinkPrefix(basePath);
    if (
        categoryByPath?.categoryLink &&
        basePath !== categoryByPath.categoryLink
    ) {
        return basePath;
    }

    return undefined;
}

function addBooleanFilters(
    params: URLParams,
    filterKeys: CheckboxFilterKeys[],
    filters: SearchServiceFilters,
) {
    filterKeys.forEach((filterKey) => {
        const value = getBoolean(params, getMappingKeyName(filterKey));
        if (value) filters[filterKey] = value;
    });
}

function addRangeFilters(
    params: URLParams,
    filterKeys: RangeFilterKeys[],
    filters: SearchServiceFilters,
) {
    filterKeys.forEach((filterKey) => {
        const value = getNumberRange(
            params,
            getMappingKeyName(filterKey),
            RANGE_DIVISORS[filterKey],
        );
        if (value) filters[filterKey] = value;
    });
}

function addStringArrayFilters(
    params: URLParams,
    filterKeys: SearchListFilterKeys[],
    filters: SearchServiceFilters,
    decodeFilterKeys: SearchListFilterKeys[],
) {
    filterKeys.forEach((filterKey) => {
        const value = getStringArray(params, getMappingKeyName(filterKey));

        if (value) {
            const needEncode = decodeFilterKeys.includes(filterKey);
            filters[filterKey] = needEncode
                ? value.map((sv) => decodePathValue(sv)!)
                : value;
        }
    });
}

/** The location levels a `location-{slug}` page resolves from GraphQL. */
export interface ResolvedPageLocation {
    Suburb?: string | null;
    District?: string | null;
    City?: string | null;
    Region?: string | null;
    State?: string | null;
    Country?: string | null;
}

export function getLocation(params: URLParams): SearchLocation | undefined {
    function decodeLocation(value?: string) {
        if (!value) return value;
        return toTitleCase(value);
    }

    // get location state from the search parameters
    const suburb = decodeLocation(
        getString(params, getMappingKeyName('suburb')),
    );
    const district = decodeLocation(
        getString(params, getMappingKeyName('district')),
    );
    const city = decodeLocation(getString(params, getMappingKeyName('city')));
    const region = decodeLocation(
        getString(params, getMappingKeyName('region')),
    );
    const state = decodeLocation(getString(params, getMappingKeyName('state')));
    const country = decodeLocation(
        getString(params, getMappingKeyName('country')),
    );

    if (!suburb && !district && !city && !region && !state && !country)
        return undefined;

    // The place picker writes latitude/longitude on the query string. Read them
    // for distance ranking; they must not create a location filter on their own.
    const coordinate = toSearchCoordinate(
        getString(params, 'latitude'),
        getString(params, 'longitude'),
    );

    return {
        suburb,
        district,
        city,
        region,
        state,
        country,
        ...(coordinate ? { coordinate } : {}),
    };
}

/**
 * Build the override params for a resolved `location-{slug}` page.
 *
 * The keys are URL param names, not property names, because
 * `parseURLToESQueryParams` merges these over the parsed URL params and
 * `getLocation` then reads each level back through its URL param name. The
 * state level is the only one whose URL name differs from its property name.
 */
export function buildLocationOverrideParams(
    location: ResolvedPageLocation,
): URLParams {
    return {
        [LOCATION_SEARCH_PARAM_KEYS.suburb]: location.Suburb ?? '',
        [LOCATION_SEARCH_PARAM_KEYS.district]: location.District ?? '',
        [LOCATION_SEARCH_PARAM_KEYS.city]: location.City ?? '',
        [LOCATION_SEARCH_PARAM_KEYS.region]: location.Region ?? '',
        [LOCATION_SEARCH_PARAM_KEYS.state]: location.State ?? '',
        [LOCATION_SEARCH_PARAM_KEYS.country]: location.Country ?? '',
    };
}

function getMappingKeyName(key: string, isSuppliersPage = false): string {
    // On suppliers page, use 'brands' instead of 'company' for professionals filter
    if (isSuppliersPage && key === 'professionals') {
        return 'brands';
    }
    return URL_PARAM_MAP[key] ?? key;
}

function encodePathValue(value: string): string {
    return value.toLocaleLowerCase().replace(/\s/g, '-');
}

export function decodePathValue(value?: string) {
    if (!value) return value;
    return toTitleCase(value.replace(/-/g, ' '));
}

/**
 * Title-case a string (first letter of each word).
 */
function toTitleCase(value: string): string {
    return value.replace(/(^\w{1})|(\s+\w{1})/g, (letter) => {
        return letter.toUpperCase();
    });
}
